import { useMemo, useState, type ReactNode } from 'react';

import { Icon } from './Icon';

export const INPUT_CLASS =
  'w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2';

export const PAGE_SIZE_OPTIONS = [6, 12, 24] as const;
export const TABLE_PAGE_SIZE_OPTIONS = [10, 25, 50] as const;
export type SortDirection = 'asc' | 'desc';

export function SearchBar({
  value,
  onChange,
  placeholder = 'Search...',
  className = '',
}: {
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
  className?: string;
}) {
  return (
    <div className={`relative ${className}`}>
      <input
        type="text"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className={INPUT_CLASS}
      />
    </div>
  );
}

export function FilterTabs<T extends string>({
  filters,
  active,
  onChange,
  counts,
}: {
  filters: { value: T; label: string }[];
  active: T;
  onChange: (value: T) => void;
  counts?: Partial<Record<T, number>>;
}) {
  return (
    <div className="flex flex-wrap gap-2">
      {filters.map((f) => (
        <button
          key={f.value}
          onClick={() => onChange(f.value)}
          className={`rounded-full px-3 py-1 text-xs font-medium transition-colors ${
            active === f.value
              ? 'bg-primary text-primary-foreground'
              : 'bg-muted text-muted-foreground hover:bg-accent'
          }`}
        >
          {f.label}
          {counts?.[f.value] != null && <span className="ml-1">({counts[f.value]})</span>}
        </button>
      ))}
    </div>
  );
}

export function SortableHeader<T extends string>({
  label,
  sortKey,
  activeSortKey,
  direction,
  onSort,
  align = 'left',
  className = '',
}: {
  label: string;
  sortKey: T;
  activeSortKey: T;
  direction: SortDirection;
  onSort: (key: T) => void;
  align?: 'left' | 'right' | 'center';
  className?: string;
}) {
  const isActive = activeSortKey === sortKey;
  const icon = !isActive ? (
    <Icon
      name="streamline:interface-arrows-data-transfer-vertical-arrow-square-data-internet-transfer-network-vertical"
      className="h-3 w-3 opacity-50"
    />
  ) : direction === 'asc' ? (
    <Icon name="streamline:interface-arrows-button-up-arrow-up-keyboard" className="h-3 w-3" />
  ) : (
    <Icon name="streamline:interface-arrows-button-down-arrow-down-keyboard" className="h-3 w-3" />
  );

  const thAlignClass = align === 'right' ? 'text-right' : align === 'center' ? 'text-center' : 'text-left';
  const buttonAlignClass =
    align === 'right' ? 'justify-end' : align === 'center' ? 'justify-center' : 'justify-start';

  return (
    <th
      className={`px-3 py-2.5 text-xs font-medium text-muted-foreground ${thAlignClass} ${className}`.trim()}
    >
      <button
        type="button"
        onClick={() => onSort(sortKey)}
        className={`inline-flex w-full items-center gap-1.5 transition-colors hover:text-foreground ${buttonAlignClass}`}
      >
        <span>{label}</span>
        {icon}
      </button>
    </th>
  );
}

type TableAlign = 'left' | 'right' | 'center';

export interface DataTableColumn<Row, SortKey extends string = string> {
  id: string;
  label: string;
  align?: TableAlign;
  sortable?: boolean;
  sortKey?: SortKey;
  headerClassName?: string;
  cellClassName?: string;
  renderCell: (row: Row) => ReactNode;
}

export function DataTable<Row, SortKey extends string = string>({
  columns,
  rows,
  rowKey,
  sort,
  emptyMessage = 'No data found.',
  rowClassName,
  onRowClick,
}: {
  columns: DataTableColumn<Row, SortKey>[];
  rows: Row[];
  rowKey: (row: Row) => string;
  sort?: {
    key: SortKey;
    direction: SortDirection;
    onSort: (key: SortKey) => void;
  };
  emptyMessage?: string;
  rowClassName?: (row: Row) => string;
  onRowClick?: (row: Row) => void;
}) {
  const alignClass = (align: TableAlign | undefined) => (
    align === 'right' ? 'text-right' : align === 'center' ? 'text-center' : 'text-left'
  );

  return (
    <div className="overflow-x-auto rounded-xl border border-border">
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b border-border bg-muted/50 text-left">
            {columns.map((column) => {
              if (column.sortable && column.sortKey && sort) {
                return (
                  <SortableHeader<SortKey>
                    key={column.id}
                    label={column.label}
                    sortKey={column.sortKey}
                    activeSortKey={sort.key}
                    direction={sort.direction}
                    onSort={sort.onSort}
                    align={column.align}
                    className={column.headerClassName ?? ''}
                  />
                );
              }

              return (
                <th
                  key={column.id}
                  className={`px-3 py-2.5 text-xs font-medium text-muted-foreground ${alignClass(column.align)} ${column.headerClassName ?? ''}`.trim()}
                >
                  {column.label}
                </th>
              );
            })}
          </tr>
        </thead>
        <tbody className="divide-y divide-border">
          {rows.length === 0 ? (
            <tr>
              <td
                colSpan={columns.length}
                className="px-3 py-12 text-center text-sm text-muted-foreground"
              >
                {emptyMessage}
              </td>
            </tr>
          ) : (
            rows.map((row) => (
              <tr
                key={rowKey(row)}
                className={`hover:bg-muted/30 transition-colors ${onRowClick ? 'cursor-pointer' : ''} ${rowClassName?.(row) ?? ''}`.trim()}
                onClick={onRowClick ? () => onRowClick(row) : undefined}
              >
                {columns.map((column) => (
                  <td
                    key={`${rowKey(row)}:${column.id}`}
                    className={`px-3 py-2.5 ${alignClass(column.align)} ${column.cellClassName ?? ''}`.trim()}
                  >
                    {column.renderCell(row)}
                  </td>
                ))}
              </tr>
            ))
          )}
        </tbody>
      </table>
    </div>
  );
}

export function PageNumbers({
  currentPage,
  totalPages,
  onPageChange,
}: {
  currentPage: number;
  totalPages: number;
  onPageChange: (page: number) => void;
}) {
  const pages: (number | 'ellipsis')[] = [];
  if (totalPages <= 7) {
    for (let i = 1; i <= totalPages; i++) pages.push(i);
  } else {
    pages.push(1);
    if (currentPage > 3) pages.push('ellipsis');
    const start = Math.max(2, currentPage - 1);
    const end = Math.min(totalPages - 1, currentPage + 1);
    for (let i = start; i <= end; i++) pages.push(i);
    if (currentPage < totalPages - 2) pages.push('ellipsis');
    pages.push(totalPages);
  }

  return (
    <div className="flex items-center gap-0.5">
      {pages.map((p, i) =>
        p === 'ellipsis' ? (
          <span key={`e-${i}`} className="px-1.5 text-sm text-muted-foreground">
            ...
          </span>
        ) : (
          <button
            key={p}
            onClick={() => onPageChange(p)}
            className={`min-w-[28px] rounded-md px-1.5 py-1 text-sm font-medium transition-colors ${
              p === currentPage
                ? 'bg-primary text-primary-foreground'
                : 'text-muted-foreground hover:text-foreground hover:bg-accent'
            }`}
          >
            {p}
          </button>
        ),
      )}
    </div>
  );
}

export function PaginationControls({
  page,
  setPage,
  pageSize,
  setPageSize,
  totalItems,
  label,
  pageSizeOptions = TABLE_PAGE_SIZE_OPTIONS as unknown as readonly number[],
}: {
  page: number;
  setPage: (p: number | ((prev: number) => number)) => void;
  pageSize: number;
  setPageSize: (s: number) => void;
  totalItems: number;
  label: string;
  pageSizeOptions?: readonly number[];
}) {
  const totalPages = Math.ceil(totalItems / pageSize);
  const safePage = Math.min(page, Math.max(1, totalPages));
  const rangeStart = totalItems === 0 ? 0 : (safePage - 1) * pageSize + 1;
  const rangeEnd = Math.min(safePage * pageSize, totalItems);

  if (totalItems === 0) return null;

  return (
    <div className="mt-6 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
      <span className="text-sm text-muted-foreground">
        Showing {rangeStart}-{rangeEnd} of {totalItems} {label}
      </span>
      <div className="flex items-center gap-3">
        <div className="flex items-center gap-1.5">
          <span className="text-sm text-muted-foreground">Per page</span>
          <select
            value={pageSize}
            onChange={(e) => {
              setPageSize(Number(e.target.value));
              setPage(1);
            }}
            className="rounded-md border border-input bg-background pl-2 pr-8 py-1 text-sm"
          >
            {pageSizeOptions.map((s) => (
              <option key={s} value={s}>
                {s}
              </option>
            ))}
          </select>
        </div>
        {totalPages > 1 && (
          <div className="flex items-center gap-1.5">
            <button
              onClick={() => setPage(1)}
              disabled={safePage === 1}
              className="rounded-md border border-border px-2 py-1 text-sm text-muted-foreground hover:text-foreground disabled:opacity-40 transition-colors"
            >
              First
            </button>
            <button
              onClick={() => setPage((p: number) => Math.max(1, p - 1))}
              disabled={safePage === 1}
              className="rounded-md border border-border p-1.5 text-muted-foreground hover:text-foreground disabled:opacity-40 transition-colors"
            >
              <span aria-hidden className="text-xs">&lt;</span>
            </button>
            <PageNumbers currentPage={safePage} totalPages={totalPages} onPageChange={setPage} />
            <button
              onClick={() => setPage((p: number) => Math.min(totalPages, p + 1))}
              disabled={safePage === totalPages}
              className="rounded-md border border-border p-1.5 text-muted-foreground hover:text-foreground disabled:opacity-40 transition-colors"
            >
              <span aria-hidden className="text-xs">&gt;</span>
            </button>
            <button
              onClick={() => setPage(totalPages)}
              disabled={safePage === totalPages}
              className="rounded-md border border-border px-2 py-1 text-sm text-muted-foreground hover:text-foreground disabled:opacity-40 transition-colors"
            >
              Last
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

export function ViewModeToggle({
  viewMode,
  onChange,
}: {
  viewMode: 'grid' | 'list';
  onChange: (mode: 'grid' | 'list') => void;
}) {
  return (
    <div className="flex rounded-lg border border-border">
      <button
        onClick={() => onChange('grid')}
        className={`rounded-l-lg p-2 transition-colors ${
          viewMode === 'grid'
            ? 'bg-primary text-primary-foreground'
            : 'text-muted-foreground hover:text-foreground'
        }`}
      >
        <span className="text-xs font-medium">Grid</span>
      </button>
      <button
        onClick={() => onChange('list')}
        className={`rounded-r-lg p-2 transition-colors ${
          viewMode === 'list'
            ? 'bg-primary text-primary-foreground'
            : 'text-muted-foreground hover:text-foreground'
        }`}
      >
        <span className="text-xs font-medium">List</span>
      </button>
    </div>
  );
}

/**
 * @deprecated Prefer `useDataList` from `@ascend/shared/hooks` for new code —
 * it bundles debounced search, filter tabs, sort, pagination, view-mode and
 * auto page-reset into a single hook with a ready-made `params` object for
 * server-side paginated queries.
 *
 * Kept for compatibility with `mocked/admin` and `mocked/client` reference apps.
 */
export function useTableControls(defaultPageSize: number = TABLE_PAGE_SIZE_OPTIONS[0]) {
  const [query, setQuery] = useState('');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState<number>(defaultPageSize);

  const resetPage = () => setPage(1);

  return {
    query,
    setQuery,
    page,
    setPage,
    pageSize,
    setPageSize,
    resetPage,
  };
}

export function usePaginatedRows<T>(rows: T[], page: number, pageSize: number) {
  return useMemo(() => {
    const totalPages = Math.max(1, Math.ceil(rows.length / pageSize));
    const safePage = Math.min(page, totalPages);
    return {
      rows: rows.slice((safePage - 1) * pageSize, safePage * pageSize),
      safePage,
      totalPages,
    };
  }, [rows, page, pageSize]);
}

export function usePagination(defaultPageSize = 10) {
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(defaultPageSize);

  function paginate<T>(items: T[]): { paginated: T[]; totalItems: number } {
    const totalPages = Math.ceil(items.length / pageSize);
    const safePage = Math.min(page, Math.max(1, totalPages));
    return {
      paginated: items.slice((safePage - 1) * pageSize, safePage * pageSize),
      totalItems: items.length,
    };
  }

  function resetPage() {
    setPage(1);
  }

  return { page, setPage, pageSize, setPageSize, paginate, resetPage };
}
